type CaseStage = {
  step: number;
  label: string;
  description: string;
  icon: string;
};

const STAGES: CaseStage[] = [
  { step: 0, label: 'Determine Eligibility', description: 'Client account created & eligibility being assessed', icon: 'ri-search-eye-line' },
  { step: 1, label: 'Documentation Review', description: 'Your case documents are being reviewed', icon: 'ri-file-search-line' },
  { step: 2, label: 'Filing Preparation', description: 'Legal filings are being prepared', icon: 'ri-draft-line' },
  { step: 3, label: 'Filed with CBP', description: 'Refund claim submitted to Customs & Border Protection', icon: 'ri-government-line' },
  { step: 4, label: 'Under CBP Review', description: 'CBP is reviewing your refund claim', icon: 'ri-time-line' },
  { step: 5, label: 'Resolution', description: 'Refund has been approved or settled', icon: 'ri-checkbox-circle-line' },
];

interface CaseStatusTrackerProps {
  caseStatus: number;
  eligibilityAuthorized?: boolean;
}

export default function CaseStatusTracker({ caseStatus, eligibilityAuthorized = false }: CaseStatusTrackerProps) {
  const safeStatus = Math.max(0, Math.min(caseStatus ?? 0, 5));
  const currentStage = STAGES[safeStatus];

  return (
    <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-8">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-2xl font-black text-white">Case Status</h3>
        <div className="bg-[#FDB913]/20 border border-[#FDB913]/40 rounded-full px-4 py-1">
          <span className="text-[#FDB913] text-xs font-black tracking-widest uppercase">
            Step {safeStatus + 1} of 6
          </span>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <span className="text-white/60 text-xs">Overall Progress</span>
          <span className="text-[#FDB913] text-xs font-black">{Math.round(((safeStatus) / 5) * 100)}%</span>
        </div>
        <div className="w-full bg-white/10 rounded-full h-2">
          <div
            className="bg-gradient-to-r from-[#FDB913] to-[#e6a800] h-2 rounded-full transition-all duration-700"
            style={{ width: `${(safeStatus / 5) * 100}%` }}
          />
        </div>
      </div>

      {/* Active stage highlight */}
      <div className="bg-gradient-to-br from-[#FDB913]/20 to-[#FDB913]/5 border-2 border-[#FDB913]/50 rounded-xl p-5 mb-6">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 flex items-center justify-center bg-[#FDB913]/30 rounded-xl border border-[#FDB913]/60 flex-shrink-0">
            <i className={`${currentStage.icon} text-[#FDB913] text-2xl`}></i>
          </div>
          <div>
            <p className="text-white/60 text-xs font-bold uppercase tracking-widest mb-1">Current Stage</p>
            <p className="text-white font-black text-lg leading-tight">{currentStage.label}</p>
            <p className="text-white/60 text-sm mt-1">{currentStage.description}</p>
          </div>
        </div>
      </div>

      {/* All stages */}
      <div className="space-y-3">
        {STAGES.map((stage) => {
          const isDone = stage.step < safeStatus;
          const isCurrent = stage.step === safeStatus;
          const isPending = stage.step > safeStatus;
          // Step 0 (Determine Eligibility) turns green when attorney authorizes eligibility
          const isEligibilityGreen = stage.step === 0 && eligibilityAuthorized;

          return (
            <div
              key={stage.step}
              className={`flex items-center gap-4 p-3 rounded-xl transition-all ${
                isEligibilityGreen
                  ? 'bg-green-500/10 border border-green-500/30'
                  : isCurrent
                  ? 'bg-[#FDB913]/10 border border-[#FDB913]/30'
                  : isDone
                  ? 'bg-white/5 border border-white/10'
                  : 'opacity-40'
              }`}
            >
              {/* Step indicator */}
              <div className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 text-xs font-black border-2 ${
                isEligibilityGreen
                  ? 'bg-green-500/30 border-green-500/60 text-green-400'
                  : isDone
                  ? 'bg-green-500/30 border-green-500/60 text-green-400'
                  : isCurrent
                  ? 'bg-[#FDB913]/30 border-[#FDB913]/60 text-[#FDB913]'
                  : 'bg-white/5 border-white/20 text-white/40'
              }`}>
                {(isDone || isEligibilityGreen) ? <i className="ri-check-line text-sm"></i> : stage.step + 1}
              </div>

              <div className="flex-1 min-w-0">
                <p className={`text-sm font-black truncate ${
                  isEligibilityGreen ? 'text-green-400' : isCurrent ? 'text-white' : isDone ? 'text-white/80' : 'text-white/40'
                }`}>
                  {stage.label}
                </p>
                {isEligibilityGreen && (
                  <p className="text-green-400/60 text-xs mt-0.5">Eligibility confirmed by your attorney</p>
                )}
              </div>

              {(isDone || isEligibilityGreen) && (
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                  <i className="ri-checkbox-circle-fill text-green-400 text-base"></i>
                </div>
              )}
              {isCurrent && !isEligibilityGreen && (
                <div className="w-2 h-2 bg-[#FDB913] rounded-full animate-pulse flex-shrink-0"></div>
              )}
              {isPending && !isEligibilityGreen && (
                <div className="w-2 h-2 bg-white/20 rounded-full flex-shrink-0"></div>
              )}
            </div>
          );
        })}
      </div>

      <p className="text-white/40 text-xs text-center mt-5 leading-relaxed">
        Your attorney updates this status as your case progresses. Questions?{' '}
        <a href="tel:+15093960741" className="text-[#FDB913] hover:text-white transition-colors cursor-pointer">Call us.</a>
      </p>
    </div>
  );
}
